import { useCallback, useRef } from 'react';
import { css } from '@emotion/react';
import dayjs from 'dayjs';
import { Trans, useTranslation, i18next } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { CareCreditChip, CareCreditPatientData } from '@frontend/payments-care-credit';
import { currency, Text, useTooltip } from '@frontend/design-system';
import { useSunbitStatusQuery } from '../../../../hooks/use-sunbit-status-query';
import { useValidPhoneNumberFromInvoice } from '../../../../hooks/use-valid-phone-number-from-invoice';
import SunbitIcon from '../../../assets/sunbit.svg?react';
import { useCollectPaymentMultiContext } from '../../../collect-payment-multi.context';
import { useScopedTrackingIds } from '../../../hooks/use-scoped-tracking-ids';
import { useValidEmailFromInvoice } from '../../../hooks/use-valid-email-from-invoice';
import { PAYMENT_MULTI_STEPS } from '../../../utils/steps';
import { PaymentMethodButton, PaymentMethodButtonProps } from '../../atoms/payment-method-button';
import { getCareCreditPaymentMethodState } from './care-credit-payment-method-state';

const customerPhoneNumberError = i18next.t('No valid phone number', { ns: 'payments' });
const customerEmailError = i18next.t("No email on file. Add one to customer's contact information to send request.", {
  ns: 'payments',
});

const ShareInEmail: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { firstValidEmail } = useValidEmailFromInvoice(invoice);

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.shareInEmail}` });

  const {
    Tooltip: EmailTooltip,
    triggerProps: EmailTooltipTriggerProps,
    tooltipProps: EmailTooltipProps,
  } = useTooltip();

  return (
    <PaymentMethodButton
      trackingId={trackingId}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.shareInEmail });
        onClick?.(e);
      }}
      iconName='email'
      {...(!firstValidEmail ? EmailTooltipTriggerProps : {})}
      {...props}
      disabled={invoiceIsPaid || !firstValidEmail || props.disabled}
    >
      {t('Share in Email')}
      <EmailTooltip {...EmailTooltipProps}>{customerEmailError}</EmailTooltip>
    </PaymentMethodButton>
  );
};
const ShareInMessage: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);
  const messagingEnabled = useMessagingFeaturesEnabled();

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.shareInMessage}` });

  const { Tooltip: TtpTooltip, triggerProps: ttpTooltipTriggerProps, tooltipProps: ttpTooltipProps } = useTooltip();

  const tooltipMessage = !messagingEnabled
    ? i18next.t('Messaging is disabled for this location', { ns: 'payments' })
    : customerPhoneNumberError;
  const showTooltip = !messagingEnabled || !firstValidPhoneNumber;

  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.shareInMessage });
        onClick?.(e);
      }}
      trackingId={trackingId}
      iconName='messaging'
      {...(showTooltip ? ttpTooltipTriggerProps : {})}
      {...props}
      disabled={invoiceIsPaid || !messagingEnabled || !firstValidPhoneNumber || props.disabled}
    >
      {t('Text-to-Pay')}
      <TtpTooltip {...ttpTooltipProps}>{tooltipMessage}</TtpTooltip>
    </PaymentMethodButton>
  );
};

const SelectTerminal: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.selectTerminal}` });
  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.selectTerminal });
        onClick?.(e);
      }}
      iconName='credit-card'
      trackingId={trackingId}
      {...props}
      disabled={invoiceIsPaid || props.disabled}
    >
      {t('Pay at Terminal')}
    </PaymentMethodButton>
  );
};

const CardOnFile: React.FC<PaymentMethodButtonProps & { enableACHOnFile: boolean }> = ({
  onClick,
  enableACHOnFile,
  ...props
}) => {
  const { t } = useTranslation('payments');
  const {
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.cardOnFile}` });

  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.cardOnFile });
        onClick?.(e);
      }}
      iconName='user'
      trackingId={trackingId}
      {...props}
      disabled={invoiceIsPaid || props.disabled}
    >
      {enableACHOnFile ? t('Card/ACH on File') : t('Card on File')}
    </PaymentMethodButton>
  );
};

const ManualCardEntry: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { trackingId } = useScopedTrackingIds({
    trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.manualCardEntry}`,
  });

  return (
    <PaymentMethodButton
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        goNextStep({ orStep: PAYMENT_MULTI_STEPS.manualCardEntry });
        onClick?.(e);
      }}
      iconName='edit-small'
      trackingId={trackingId}
      {...props}
      disabled={invoiceIsPaid || props.disabled}
    >
      {t('Manual Card Entry')}
    </PaymentMethodButton>
  );
};

const styles = {
  icon: css`
    margin-right: var(--fabric-space-offset-sm);
    width: var(--fabric-size-icon-sm);
    height: var(--fabric-size-icon-sm);
  `,
  sunbitIcon: (isAcceptableSunbitAmount: boolean) =>
    css`
      ${!isAcceptableSunbitAmount && `opacity: 0.25;`};
      margin: 0 auto;
    `,
};

const MINIMUM_SUNBIT_AMOUNT = 6000;

const Sunbit: React.FC<PaymentMethodButtonProps> = ({ onClick, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    invoiceIsPaid,
    multiStep: { goNextStep },
  } = useCollectPaymentMultiContext();

  const { data: sunbitStatus } = useSunbitStatusQuery({ locationId: invoice?.locationId });

  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  const isAcceptableSunbitAmount = !!invoice?.billedAmount && invoice.billedAmount >= MINIMUM_SUNBIT_AMOUNT;

  const {
    Tooltip: SunbitTooltip,
    triggerProps: sunbitTooltipTriggerProps,
    tooltipProps: sunbitTooltipProps,
  } = useTooltip();

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${PAYMENT_MULTI_STEPS.sunbit}` });

  const isSunbitStatusEnabled = sunbitStatus?.status && ['ACTIVATED', 'SUBMITTED'].includes(sunbitStatus?.status);

  let tooltipMessage = !firstValidPhoneNumber ? customerPhoneNumberError : '';

  if (!tooltipMessage && !isSunbitStatusEnabled) {
    tooltipMessage = t('Sunbit is not available. Status: {{status}}.', { status: sunbitStatus?.status ?? 'UNKNOWN' });
  }

  return (
    <PaymentMethodButton
      onClick={() => goNextStep({ orStep: PAYMENT_MULTI_STEPS.sunbit })}
      trackingId={trackingId}
      {...(!firstValidPhoneNumber || !!tooltipMessage ? sunbitTooltipTriggerProps : {})}
      {...props}
      disabled={
        invoiceIsPaid || !isAcceptableSunbitAmount || !firstValidPhoneNumber || props.disabled || !isSunbitStatusEnabled
      }
    >
      <div>
        <div>
          <SunbitIcon css={[styles.icon, styles.sunbitIcon(isAcceptableSunbitAmount)]} />
          <div>{t('Sunbit')}</div>
        </div>
        {!isAcceptableSunbitAmount && (
          <Text size='small' color='disabled'>
            {t('($60.00 min.)')}
          </Text>
        )}
        <SunbitTooltip {...sunbitTooltipProps}>{tooltipMessage}</SunbitTooltip>
      </div>
    </PaymentMethodButton>
  );
};

const careCreditStyles = {
  amountLine: css`
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: var(--fabric-space-gap-xxs);
    font-weight: var(--fabric-font-weight-regular);
    font-size: var(--fabric-font-size-xs);
    align-items: center;

    .icon {
      flex-shrink: 0;
    }
  `,
  italicLine: css`
    margin-top: var(--fabric-space-gap-xxs);
    font-style: italic;
    font-weight: var(--fabric-font-weight-regular);
    font-size: var(--fabric-font-size-sm);
  `,
};

const CareCredit: React.FC<
  PaymentMethodButtonProps & { careCreditData?: CareCreditPatientData; isApplicationDeclined?: boolean }
> = ({ onClick, careCreditData, isApplicationDeclined, ...props }) => {
  const { t } = useTranslation('payments');
  const {
    invoice,
    multiStep: { goNextStep },
    invoiceIsPaid,
    isCareCreditLoading,
    careCreditChipStatus,
  } = useCollectPaymentMultiContext();

  const { firstValidPhoneNumber } = useValidPhoneNumberFromInvoice(invoice);

  const { applicantStatus, chipStatus, isDisabledByStatus, isUnknown, statusWithErrors } =
    getCareCreditPaymentMethodState({
      careCreditData,
      careCreditChipStatus,
      isApplicationDeclined: !!isApplicationDeclined,
    });

  const { trackingId } = useScopedTrackingIds({ trackingId: `payment-methods--${applicantStatus}` });

  const { tooltipProps, triggerProps, Tooltip } = useTooltip({
    placement: 'bottom-start',
  });

  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (applicantStatus === 'CARDHOLDER') {
        goNextStep({
          orStep: PAYMENT_MULTI_STEPS.careCreditCollect,
        });
      } else if (applicantStatus === 'PREAPPROVED') {
        goNextStep({
          orStep: PAYMENT_MULTI_STEPS.careCreditPreapprovedDisclosure,
        });
      } else if (applicantStatus === 'PREQUALIFICATION_NEEDED') {
        goNextStep({
          orStep: PAYMENT_MULTI_STEPS.careCreditEnrollDisclosure,
        });
      } else if (isUnknown) {
        goNextStep({
          orStep: PAYMENT_MULTI_STEPS.careCreditVerifyStatus,
        });
      } else {
        console.warn('Unknown applicant status', applicantStatus);
      }
      onClick?.(e);
    },
    [onClick, applicantStatus, goNextStep, isUnknown]
  );

  const showCareCreditAmount = ['CARDHOLDER', 'PREAPPROVED'].includes(applicantStatus || '');

  let tooltipMessage = '';
  if (isUnknown) {
    tooltipMessage = t('Click to verify CareCredit status');
  } else if (statusWithErrors === 'ERROR') {
    tooltipMessage = t('Unable to retrieve CareCredit eligibility status at this time');
  } else if (!firstValidPhoneNumber && applicantStatus === 'PREAPPROVED') {
    tooltipMessage = t('Customer does not have a phone number that can receive text messages.');
  }

  return (
    <PaymentMethodButton
      trackingId={trackingId}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        handleClick(e);
      }}
      iconName='care-credit'
      chip={
        !isCareCreditLoading &&
        chipStatus !== 'ERROR' && (
          <CareCreditChip status={chipStatus} size='tiny' containerRef={buttonRef} isResponsive />
        )
      }
      tooltip={tooltipMessage}
      {...props}
      ref={buttonRef}
      loading={isCareCreditLoading}
      disabled={
        props.disabled ||
        invoiceIsPaid ||
        isDisabledByStatus ||
        isCareCreditLoading ||
        (!firstValidPhoneNumber && applicantStatus === 'PREAPPROVED')
      }
    >
      <div>{t('CareCredit')}</div>
      {showCareCreditAmount && (
        <>
          <div>
            <div css={careCreditStyles.amountLine}>
              {careCreditData?.status === 'CARDHOLDER' && (
                <span>
                  <Trans
                    t={t}
                    i18nKey='<b>{{amount}}</b> <span>available</span>'
                    values={{ amount: currency(careCreditData?.amount || 0, { decimals: 0 }) }}
                    components={{ b: <b />, span: <span /> }}
                  />
                </span>
              )}

              {careCreditData?.asOfDate && careCreditData?.status !== 'PREAPPROVED' && (
                <>
                  <span className='icon' {...triggerProps}>
                    <Icon name='info' size={16} />
                  </span>
                  <Tooltip {...tooltipProps}>
                    {t('Last updated {{date}} at {{time}}', {
                      date: dayjs(careCreditData?.asOfDate).format('MMM DD, YYYY'),
                      time: dayjs(careCreditData?.asOfDate).format('h:mm A'),
                    })}
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </PaymentMethodButton>
  );
};

export const PaymentMethodButtons = {
  ShareInEmail,
  ShareInMessage,
  SelectTerminal,
  CardOnFile,
  ManualCardEntry,
  Sunbit,
  CareCredit,
};
